import EventTypeSetting from 'domains/models/event_type_setting';
import { z } from 'zod';
import { MailSenderTypeEnum } from './enum';

export type EventTypeSettingUpdateBody = z.infer<
  typeof schemaEventTypeSettingUpdateBody
>;
export const schemaEventTypeSettingUpdateBody = z
  .object({
    isCalendarInvite: z.boolean(),
    isMailConfirm: z.boolean(),
    isMailCancel: z.boolean(),
    isMailRemind: z.boolean(),
    isMailFollowUp: z.boolean(),
    isTextRemind: z.boolean(),
    isIncludeAllDay: z.boolean(),
    isExcludeHoliday: z.boolean(),
    isSlackIntegration: z.boolean(),
    isCompanyRequired: z.boolean(),
    isCompanyHidden: z.boolean(),
    isShareHostMail: z.boolean(),
    isCcEmailsHidden: z.boolean(),
    isCcEmailsRequired: z.boolean(),
    isMessageHidden: z.boolean(),
    isMessageRequired: z.boolean(),
    isTextRemindNumberRequired: z.boolean(),
    isPaymentCard: z.boolean(),
    isPaymentOnsite: z.boolean(),
    isMultiselectService: z.boolean(),
    isIncludeServiceJP: z.boolean(),
    isCalendarIncludeForm: z.boolean(),
    isMailIncludeForm: z.boolean(),
    mailSenderType: MailSenderTypeEnum,
    isEnableGuestUpdate: z.boolean(),
    isReserveBuffer: z.boolean(),
    isRestrictDuration: z.boolean(),
    isRestrictCount: z.boolean(),
    bufferName: z.string().nullable(),
    isBufferResource: z.boolean(),
    isBookingRequest: z.boolean(),
    isReserveSchedule: z.boolean(),
  })
  .partial();

export type EventTypeSettingPublicItem = z.infer<
  typeof schemaEventTypeSettingPublicItem
>;
export const schemaEventTypeSettingPublicItem = z.object({
  isIncludeAllDay: z.boolean(),
  isExcludeHoliday: z.boolean(),
  isCompanyRequired: z.boolean(),
  isCompanyHidden: z.boolean(),
  isCcEmailsHidden: z.boolean(),
  isCcEmailsRequired: z.boolean(),
  isMessageHidden: z.boolean(),
  isMessageRequired: z.boolean(),
  isTextRemindNumberRequired: z.boolean(),
  isTextRemind: z.boolean(),
  isPaymentCard: z.boolean(),
  isPaymentOnsite: z.boolean(),
  isMultiselectService: z.boolean(),
  isIncludeServiceJP: z.boolean(),
  isBookingRequest: z.boolean(),
  isBufferResource: z.boolean(),
});

export type EventTypeSettingItem = z.infer<typeof schemaEventTypeSettingItem>;
export const schemaEventTypeSettingItem = z.object({
  isCalendarInvite: z.boolean(),
  isMailConfirm: z.boolean(),
  isMailCancel: z.boolean(),
  isMailRemind: z.boolean(),
  mailRemindTiming: z.number().nullable(),
  textRemindTiming: z.number().nullable(),
  isMailFollowUp: z.boolean(),
  mailFollowUpTiming: z.number().nullable(),
  isTextRemind: z.boolean(),
  isIncludeAllDay: z.boolean(),
  isExcludeHoliday: z.boolean(),
  isCompanyRequired: z.boolean(),
  isCompanyHidden: z.boolean(),
  isCcEmailsHidden: z.boolean(),
  isCcEmailsRequired: z.boolean(),
  isMessageHidden: z.boolean(),
  isMessageRequired: z.boolean(),
  isTextRemindNumberRequired: z.boolean(),
  isShareHostMail: z.boolean(),
  isPaymentCard: z.boolean(),
  isPaymentOnsite: z.boolean(),
  isMultiselectService: z.boolean(),
  isIncludeServiceJP: z.boolean(),
  isCalendarIncludeForm: z.boolean(),
  isMailIncludeForm: z.boolean(),
  mailSenderType: MailSenderTypeEnum,
  isEnableGuestUpdate: z.boolean().optional(),
  isReserveBuffer: z.boolean(),
  isRestrictDuration: z.boolean(),
  isRestrictCount: z.boolean(),
  bufferName: z.string().nullable(),
  isBufferResource: z.boolean(),
  isBookingRequest: z.boolean(),
  isReserveSchedule: z.boolean(),
});

export const toItem = (
  eventTypeSetting: EventTypeSetting,
): EventTypeSettingItem => {
  return {
    isCalendarInvite: eventTypeSetting.isCalendarInvite,
    isMailConfirm: eventTypeSetting.isMailConfirm,
    isMailCancel: eventTypeSetting.isMailCancel,
    isMailRemind: eventTypeSetting.isMailRemind,
    mailRemindTiming: eventTypeSetting.mailRemindTiming,
    textRemindTiming: eventTypeSetting.textRemindTiming,
    isMailFollowUp: eventTypeSetting.isMailFollowUp,
    mailFollowUpTiming: eventTypeSetting.mailFollowUpTiming,
    isTextRemind: eventTypeSetting.isTextRemind,
    isIncludeAllDay: eventTypeSetting.isIncludeAllDay,
    isExcludeHoliday: eventTypeSetting.isExcludeHoliday,
    isCompanyRequired: eventTypeSetting.isCompanyRequired,
    isCompanyHidden: eventTypeSetting.isCompanyHidden,
    isCcEmailsHidden: eventTypeSetting.isCcEmailsHidden,
    isCcEmailsRequired: eventTypeSetting.isCcEmailsRequired,
    isMessageHidden: eventTypeSetting.isMessageHidden,
    isMessageRequired: eventTypeSetting.isMessageRequired,
    isTextRemindNumberRequired: eventTypeSetting.isTextRemindNumberRequired,
    isShareHostMail: eventTypeSetting.isShareHostMail,
    isPaymentCard: eventTypeSetting.isPaymentCard,
    isPaymentOnsite: eventTypeSetting.isPaymentOnsite,
    isMultiselectService: eventTypeSetting.isMultiselectService,
    isIncludeServiceJP: eventTypeSetting.isIncludeServiceJP,
    isCalendarIncludeForm: eventTypeSetting.isCalendarIncludeForm,
    isMailIncludeForm: eventTypeSetting.isMailIncludeForm,
    mailSenderType: eventTypeSetting.mailSenderType,
    isEnableGuestUpdate: eventTypeSetting.isEnableGuestUpdate,
    isReserveBuffer: eventTypeSetting.isReserveBuffer,
    isRestrictDuration: eventTypeSetting.isRestrictDuration,
    isRestrictCount: eventTypeSetting.isRestrictCount,
    bufferName: eventTypeSetting.bufferName,
    isBufferResource: eventTypeSetting.isBufferResource,
    isBookingRequest: eventTypeSetting.isBookingRequest,
    isReserveSchedule: eventTypeSetting.isReserveSchedule,
  };
};

export const toPublicItem = (
  eventTypeSetting: EventTypeSetting,
): EventTypeSettingPublicItem => {
  return {
    isIncludeAllDay: eventTypeSetting.isIncludeAllDay,
    isExcludeHoliday: eventTypeSetting.isExcludeHoliday,
    isCompanyRequired: eventTypeSetting.isCompanyRequired,
    isCompanyHidden: eventTypeSetting.isCompanyHidden,
    isCcEmailsHidden: eventTypeSetting.isCcEmailsHidden,
    isCcEmailsRequired: eventTypeSetting.isCcEmailsRequired,
    isMessageHidden: eventTypeSetting.isMessageHidden,
    isMessageRequired: eventTypeSetting.isMessageRequired,
    isTextRemind: eventTypeSetting.isTextRemind,
    isTextRemindNumberRequired: eventTypeSetting.isTextRemindNumberRequired,
    isPaymentCard: eventTypeSetting.isPaymentCard,
    isPaymentOnsite: eventTypeSetting.isPaymentOnsite,
    isMultiselectService: eventTypeSetting.isMultiselectService,
    isIncludeServiceJP: eventTypeSetting.isIncludeServiceJP,
    isBookingRequest: eventTypeSetting.isBookingRequest,
    isBufferResource: eventTypeSetting.isBufferResource,
  };
};
